import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaRegQuestionCircle } from '@react-icons/all-files/fa/FaRegQuestionCircle';
import { useState } from 'react';

import { Button } from '../../../../../components/ui/buttons/Button';
import { LDFlags } from '../../../../../constants/flags';
import { useFlag } from '../../../../../hooks';
import {
  trackFaqOpened,
  trackFaqQuestionExpanded,
} from '../../../../../services/analytics/marketplaceEvents';
import { GetLeadByExternalId200 } from '../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { UserDeal } from '../../../utils';
import { ListingStatus } from '../listingStatus';
import { useOpenPlatformChat } from '../live/conversation/PlatformChat';
import { isPlatformChatAvailable } from '../live/conversation/utils';
import { FAQ_GROUPS, FAQ_GROUP_ORDER, FaqGroupId } from './faqContent';

type MarketplaceFaqProps = {
  userDeal: UserDeal;
  lead: GetLeadByExternalId200 | undefined;
  listingStatus: ListingStatus;
};

const MarketplaceFaq = ({ userDeal, lead, listingStatus }: MarketplaceFaqProps) => {
  const faqEnabled = useFlag(LDFlags.MARKETPLACE_FAQ);
  const messagingEnabled = useFlag(LDFlags.MARKETPLACE_MESSAGING);
  const dealId = userDeal?.id || '';
  const openPlatformChat = useOpenPlatformChat();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const [isOpen, setIsOpen] = useState(false);
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);

  if (!faqEnabled) return null;

  const lienholder = {
    slug: lead?.lienholderSlug ?? userDeal?.car?.payoff?.lienholder_slug,
    name: lead?.lienholderName ?? userDeal?.car?.payoff?.lienholder_name,
  };

  const handleOpen = () => {
    setOpenQuestionId(null);
    trackFaqOpened(dealId, listingStatus, isMobile);
    setIsOpen(true);
  };

  const toggleQuestion = (questionId: string, groupId: FaqGroupId) => {
    if (openQuestionId === questionId) {
      setOpenQuestionId(null);
      return;
    }
    trackFaqQuestionExpanded(dealId, listingStatus, questionId, groupId);
    setOpenQuestionId(questionId);
  };

  return (
    <>
      <Button
        aria-label="FAQs"
        variant="bluePill"
        position="fixed"
        zIndex="sticky"
        bottom={{ base: '20px', md: '28px' }}
        right={{ base: '16px', md: '28px' }}
        h="auto"
        px={{ base: '14px', md: '18px' }}
        py={{ base: '14px', md: '12px' }}
        boxShadow="lg"
        onClick={handleOpen}
      >
        <Icon as={FaRegQuestionCircle} boxSize="22px" />
        <Text as="span" display={{ base: 'none', md: 'inline' }} ml={2} fontSize="14px">
          FAQs
        </Text>
      </Button>
      <Drawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement={isMobile ? 'bottom' : 'right'}
        size="md"
      >
        <DrawerOverlay />
        <DrawerContent
          maxH={{ base: '90dvh', md: '100%' }}
          borderTopRadius={{ base: '20px', md: undefined }}
        >
          <DrawerCloseButton top="16px" right="16px" borderRadius="full" bg="grayCoolLight" />
          <DrawerHeader borderBottomWidth="1px" borderBottomColor="grayMediumBackground" pr="64px">
            <Text fontSize="sm" fontWeight="700" textTransform="uppercase" color="royalBlue">
              Selling your lease
            </Text>
            <Text fontSize="22px" fontWeight="800" color="leaseEndBlue">
              FAQs
            </Text>
          </DrawerHeader>
          <DrawerBody px="24px" pt={0} pb="8px">
            {FAQ_GROUP_ORDER[listingStatus].map((groupId) => {
              const group = FAQ_GROUPS[groupId];
              return (
                <Box key={groupId} pt="22px">
                  <Text fontSize="17px" fontWeight="700" color="royalBlue">
                    {group.label}
                  </Text>
                  {group.questions.map((question) => {
                    const isExpanded = openQuestionId === question.id;
                    return (
                      <Box
                        key={question.id}
                        borderBottom="1px solid"
                        borderColor="grayMediumBackground"
                      >
                        <Flex
                          as="button"
                          type="button"
                          aria-expanded={isExpanded}
                          w="full"
                          py="16px"
                          align="center"
                          justify="space-between"
                          gap="16px"
                          textAlign="left"
                          onClick={() => toggleQuestion(question.id, groupId)}
                        >
                          <Text
                            as="span"
                            fontSize="15px"
                            fontWeight="600"
                            lineHeight="1.4"
                            color={isExpanded ? 'leaseEndBlue' : 'darkCharcoal'}
                          >
                            {question.question}
                          </Text>
                          <Icon
                            as={FaPlus}
                            boxSize="14px"
                            color="grayLight"
                            mr={2}
                            transform={isExpanded ? 'rotate(45deg)' : undefined}
                            transition="transform 0.2s"
                          />
                        </Flex>
                        <Collapse in={isExpanded}>
                          <Text fontSize="14px" lineHeight="1.7" color="charcoal" pb="18px">
                            {typeof question.answer === 'function'
                              ? question.answer(lienholder)
                              : question.answer}
                          </Text>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
            {messagingEnabled && isPlatformChatAvailable(lead) && (
              <Text fontSize="13px" color="grayLight" textAlign="center" py="26px">
                Still have a question?{' '}
                <Button
                  variant="link"
                  fontWeight="600"
                  fontSize="13px"
                  minW="auto"
                  verticalAlign="baseline"
                  onClick={() => {
                    setIsOpen(false);
                    openPlatformChat(lead.id);
                  }}
                >
                  Message us
                </Button>{' '}
                from your dashboard.
              </Text>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MarketplaceFaq;
